// identityCache.js
//
// Who the current session thinks it is — roles, display name, company membership — cached at login
// by App.js so the ~30 places that need one of them don't each re-read appUsers.
//
// WHY THIS EXISTS AS ITS OWN MODULE. `localStorage` is per-ORIGIN and shared by every tab.
// Impersonation (MasterAdminConsole -> "view as this user") opens a normal tab on the same origin
// and isolates only Firebase Auth, using in-memory persistence so the impersonated session dies
// with the tab. The identity cache had no such isolation, which broke in both directions:
//
//   - Writing the impersonated user's identity to localStorage overwrote the platform admin's own,
//     in every other tab they had open, and deleting those keys on logout wiped it entirely.
//   - Simply NOT writing during impersonation (the first attempt at fixing that) leaves the
//     impersonation tab reading the ADMIN's companies, which several services scope their queries
//     by — and a query scoped to companies the impersonated user does not belong to is not
//     provable against the rules, so it is rejected outright rather than returning fewer rows.
//
// `sessionStorage` is per-TAB, which is exactly the isolation impersonation needs. The
// impersonation tab writes there; everything reads sessionStorage first and falls back to
// localStorage. For an ordinary session nothing is ever written to sessionStorage, so every read
// resolves to localStorage exactly as it always did.
const IDENTITY_KEYS = ['userRoles', 'technicianName', 'companyIds'];

/**
 * Reads one identity value, preferring this tab's own (impersonation) over the shared one.
 * Returns null when absent, matching localStorage.getItem's own contract so every existing
 * `|| 'Admin'` / `|| 'null'` fallback at the call sites keeps working unchanged.
 */
export const identityItem = (key) => {
  try {
    const scoped = sessionStorage.getItem(key);
    if (scoped !== null) return scoped;
  } catch (error) {
    // Private mode / storage disabled — fall through to localStorage.
  }
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

/**
 * Caches the resolved identity. `tabScoped` writes to sessionStorage instead, so an impersonation
 * tab gets a correct identity of its own without touching what other tabs are reading.
 */
export const setIdentityCache = ({ roles, technicianName, companyIds }, { tabScoped = false } = {}) => {
  const store = tabScoped ? sessionStorage : localStorage;
  try {
    store.setItem('userRoles', JSON.stringify(roles || []));
    store.setItem('technicianName', technicianName || '');
    store.setItem('companyIds', JSON.stringify(companyIds || []));
  } catch (error) {
    console.error('Could not cache the signed-in identity:', error);
  }
};

/**
 * Clears the identity. An impersonation tab clears only its OWN copy — wiping the shared one is
 * precisely the bug this module exists to prevent.
 */
export const clearIdentityCache = ({ tabScopedOnly = false } = {}) => {
  try {
    IDENTITY_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    // nothing to clear
  }
  if (tabScopedOnly) return;
  try {
    IDENTITY_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    // nothing to clear
  }
};

/**
 * THE SIGNED-IN USER'S OWN COMPANIES, with no Firestore read.
 *
 * Lifted here on 2026-10-30 because a second module needed it. shiftClockService.js has to scope
 * its overlap check to companies the caller can provably read — a query wider than that is not
 * rejected row-by-row, it is rejected outright — and it cannot import techJobTrackingService.js,
 * which held the only copy, without creating a circular import. Returning [] is a normal outcome
 * (an unauthenticated or freshly-cleared session); every caller decides what that means for it.
 */
export const myCachedCompanyIds = () => {
  try {
    const mine = JSON.parse(identityItem('companyIds') || 'null');
    return Array.isArray(mine) ? mine : [];
  } catch (error) {
    return [];
  }
};
